import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { initialize } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import { v4 as uuidv4 } from 'uuid'
import { SorterResult } from 'antd/lib/table/interface'
import { get } from 'lodash'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { Button, Col, Row, TablePaginationConfig } from 'antd'
import Table, { ColumnsType } from 'antd/lib/table'

// reducers
import { RootState } from '../../reducers'
import { getTemplates, ITemplateDateChange, ITemplateTableItem } from '../../reducers/templates/templateActions'
import { IFormDocDateChange } from '../../reducers/formDocs/formDocsActions'

// utils
import { EDITING_TIME_MINUTES_LIMIT, FORM, PAGINATION, TEMPLATE_ORDER } from '../../utils/enums'
import { postReq } from '../../utils/request'
import { orderDirectionByTable, orderDirectionForTable } from '../../utils/helper'

// assets
import { ReactComponent as CopyIcon } from '../../assets/icons/BaseIcons/icon-copy.svg'

// components
import TemplatesPageFilter from './components/TemplatesPageFilter'
import TemplateModal from '../../components/modals/TemplateModal'

// types
import { ITemplate, ITemplateCreateModal } from '../../types/interfaces'

type Columns = ColumnsType<ITemplateTableItem>

const TemplatesPage = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const loginUserData = useSelector((state: RootState) => state.auth)
	const [isTemplateModalShowed, setTemplateModalShowed] = useState(false)

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		type: StringParam,
		usage: StringParam,
		limit: NumberParam,
		statusFilter: withDefault(StringParam, undefined),
		orderBy: withDefault(StringParam, undefined),
		orderDirection: withDefault(StringParam, 'asc'),
		page: withDefault(NumberParam, 1)
	})

	const templates = useSelector((state: RootState) => state.template.templates)
	const [processing, setProcessing] = useState(false)
	const loading = templates?.isLoading
	const backUrl = btoa(`${window.location.pathname}${window.location.search}`)

	useEffect(() => {
		dispatch(
			getTemplates(query.page, query.limit, {
				search: query.search,
				orderBy: query.orderBy,
				orderDirection: query.orderDirection,
				statusFilter: query.statusFilter
			})
		)
	}, [dispatch, query.limit, query.orderBy, query.orderDirection, query.page, query.search, query.statusFilter])

	useEffect(() => {
		const initData = {
			search: query.search || null
		}
		dispatch(initialize(FORM.TEMPLATES_PAGE_FILTER, initData))
	}, [dispatch, query.search])

	const renderDateWithAuthor = (data: IFormDocDateChange, nowEdited?: boolean) => {
		if (data && !nowEdited) {
			return (
				<>
					<span className={'text-gray-700 inline-block'}>{data.date}</span>
					<span className={'text-gray-300 mx-1'}>|</span>
					<span className={'text-gray-700'}>{data?.author || '-'}</span>
				</>
			)
		}
		return (
			<>
				<span className={'text-gray-700 inline-block'}>{t('loc:Now edited')}</span>
				<span className={'text-gray-300 mx-1'}>|</span>
				<span className={'text-gray-700'}>{data?.author || '-'}</span>
			</>
		)
	}

	const checkIfTemplateIsEditing = (form: ITemplateTableItem) => {
		const time = Math.abs(dayjs(form?.updatedAt || 0).diff(dayjs(), 'minutes'))
		if (`${form?.lastEdit?.id}` === loginUserData.authUser?.data?.id || time >= EDITING_TIME_MINUTES_LIMIT || form?.lastEdit === null) {
			return false
		}
		return true
	}

	const duplicateTemplate = async (templateId: number, record: ITemplateTableItem) => {
		try {
			const duplicatedTemplateName = `${record.name}_${uuidv4()}`
			const data = await postReq(`/api/admin/templates/${templateId}/duplicate/`, null, { name: duplicatedTemplateName })
			const newTemplateId = data.data.data?.newTemplateID
			navigate(`${t('paths:template-settings/{{templateId}}', { templateId: newTemplateId }) as string}?backUrl=${backUrl}`)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const columns: Columns = [
		{
			title: t('loc:ID'),
			width: '5rem',
			ellipsis: {
				showTitle: false
			},
			key: 'id',
			align: 'right',
			dataIndex: 'id',
			render(value) {
				return <span className={'text-gray-700'}>{value || '-'}</span>
			}
		},
		{
			title: t('loc:Názov'),
			ellipsis: {
				showTitle: false
			},
			key: 'name',
			sorter: true,
			sortOrder: query.orderBy === TEMPLATE_ORDER.NAME ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			dataIndex: 'name',
			render(value, record) {
				return (
					<span
						className={'text-gray-800 font-medium title-form-list'}
						onClick={() => {
							navigate(`${t('paths:template-builder/{{templateId}}', { templateId: record.id }) as string}?backUrl=${backUrl}`)
						}}
					>
						{value || '-'}
					</span>
				)
			}
		},
		{
			title: t('loc:Popis'),
			key: 'description',
			dataIndex: 'description',
			render(value) {
				return <span className={'text-gray-800 font-medium'}>{value || '-'}</span>
			}
		},
		{
			title: t('loc:Created'),
			ellipsis: {
				showTitle: false
			},
			key: 'createdAt',
			dataIndex: 'created',
			sorter: true,
			sortOrder: query.orderBy === TEMPLATE_ORDER.CREATED_AT ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			render(value: ITemplateDateChange) {
				return renderDateWithAuthor(value) || '-'
			}
		},
		{
			title: t('loc:Last edit'),
			ellipsis: {
				showTitle: false
			},
			key: 'updatedAt',
			dataIndex: 'lastEdit',
			sorter: true,
			sortOrder: query.orderBy === TEMPLATE_ORDER.UPDATED_AT ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			render(value: ITemplateDateChange, record) {
				return renderDateWithAuthor(value, checkIfTemplateIsEditing(record)) || '-'
			}
		},
		{
			title: '',
			ellipsis: {
				showTitle: false
			},
			width: '4rem',
			key: 'id',
			dataIndex: 'id',
			sorter: false,
			render(value, record) {
				return (
					<div className={'flex justify-end links'}>
						<Button
							type='link'
							className={'wrap mr-2'}
							icon={<CopyIcon />}
							onClick={() => {
								duplicateTemplate(value, record)
							}}
						/>
					</div>
				)
			}
		}
	]

	const onChangeTable = (pagination: TablePaginationConfig, sorter: SorterResult<any> | SorterResult<any>[]) => {
		if (!(sorter instanceof Array)) {
			let order: any = {
				orderBy: undefined,
				orderDirection: undefined
			}
			if (sorter?.order) {
				order = Array.isArray(sorter)
					? {
							orderBy: sorter[0].columnKey,
							orderDirection: orderDirectionByTable(sorter[0].order)
						}
					: {
							orderBy: sorter.columnKey,
							orderDirection: orderDirectionByTable(sorter.order)
						}
			}

			const newQuery = {
				...query,
				...order,
				limit: pagination?.pageSize,
				page: pagination?.current
			}
			setQuery(newQuery)
		}
	}

	const searchSubmit = useCallback(
		(value: object) => {
			const newQuery = {
				...query,
				search: get(value, 'search'),
				page: 1
			}
			setQuery(newQuery)
		},
		[query, setQuery]
	)

	const createNewFormTemplate = async (formTemplateData: ITemplateCreateModal) => {
		try {
			setProcessing(true)
			const formTemplate: ITemplate = {
				name: formTemplateData?.name,
				description: formTemplateData?.description,
				formType: formTemplateData?.type
			}
			const template = await postReq('/api/admin/templates/', undefined, formTemplate)
			setProcessing(false)
			setTemplateModalShowed(false)
			navigate(t(`path:/template-settings/${template?.data?.data?.templateID}?backUrl=${backUrl}`) as string)
		} catch (err: any) {
			setProcessing(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	return (
		<>
			<Row>
				<Col span={24}>
					<div className='content-wrapper'>
						<TemplatesPageFilter
							createNewTemplate={() => {
								setTemplateModalShowed(true)
							}}
							onSubmit={searchSubmit}
						/>
						<Table
							className='table-fixed mainTable'
							// 21rem = height of header + height of filter
							style={{
								borderRadius: 25,
								minHeight: 'calc(100vh - 19rem)'
							}}
							tableLayout={'fixed'}
							rowClassName={'clickable-row'}
							onChange={(pagination, sorter) => onChangeTable(pagination, sorter)}
							columns={columns}
							dataSource={templates?.tableData}
							loading={loading}
							scroll={{ y: 'calc(100vh - 19rem)' }}
							pagination={{
								showTotal: (total, [from, to]) =>
									t('loc:{{from}} - {{to}} z {{total}}', {
										total,
										from,
										to
									}),
								position: ['bottomRight'],
								defaultPageSize: PAGINATION.defaultPageSize,
								pageSizeOptions: PAGINATION.pageSizeOptions,
								pageSize: templates?.pagination?.limit,
								total: templates?.pagination?.totalCount,
								current: templates?.pagination?.page
							}}
						/>
					</div>
				</Col>
			</Row>
			<TemplateModal
				width={460}
				handleSubmit={createNewFormTemplate}
				isVisible={isTemplateModalShowed}
				title={t('loc:Create template')}
				onCancel={() => {
					setTemplateModalShowed(false)
				}}
				onConfirmBtnTitle={t('loc:Create template')}
				disabled={processing}
			/>
		</>
	)
}

export default TemplatesPage
